import React, { useState, useEffect, MouseEvent } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Drawer,
  Button
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUser } from '../../../../contexts/UserContext';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';
import { holdMessage } from '../../../../utils/securityHold';
import { formatTokenAmount } from '../../../../constants/functions';
import '@solana/wallet-adapter-react-ui/styles.css';
import './Header.css';
import { MenuIcon } from '../../../../constants/icons';
import Sidebar from '../Sidebar/Sidebar';
import { getProfilePictureUrl } from '../../../../utils/profilePicture';
import { imageAssetUrl } from '../../../../utils/publicAssets';
import { PT } from '../../../../components/portalUi';

const logo = imageAssetUrl('logo-menu.png');

type HeaderProps = { isMobile: boolean; floating?: boolean };

function Header({ isMobile, floating = false }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { logout } = useAuth();
  const { user, setUser } = useUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { publicKey, connected } = useWallet();
  const { put } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const profilePictureUrl = getProfilePictureUrl(user, imageError);

  useEffect(() => {
    const saveWalletAddress = async () => {
      if (connected && publicKey) {
        try {
          const result = await put(
            '/user/wallet-address',
            { walletAddress: publicKey.toString() },
            true
          );

          if (result.updated) {
            showSnackbar('Wallet address saved successfully.', 'success');
            setUser({ ...user, walletAddress: publicKey.toString() });
          }
        } catch (error: any) {
          // a new wallet during the 48-hour hold after a 2FA reset is refused with the
          // reason; the same wallet reconnecting is not a change and is never refused
          showSnackbar(
            holdMessage(error) ??
              error?.data?.message ??
              error.response?.data?.message ??
              'Failed to save wallet address',
            holdMessage(error) ? 'warning' : 'error'
          );
        }
      }
    };

    saveWalletAddress();
  }, [connected, publicKey]);

  useEffect(() => {
    setImageError(false);
  }, [user.uuid, user.refreshImage]);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /* Which screen the embedded arena is showing ('menu' | 'game' | 'collection' |
     'achievements'). The game reports it over postMessage (see the bridge in
     public/tcg-arena/index.html); until it does, assume 'menu' so the button is
     available rather than missing.
     We only need it to know when to get out of the way — see the floating button
     below. Same-origin only: the arena is served from our own origin, so a
     message from anywhere else is not it. */
  const [arenaScreen, setArenaScreen] = useState<string>('menu');
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; screen?: string } | null;
      if (data?.type !== 'INCRYPT_TCG_SCREEN') return;
      if (typeof data.screen === 'string') setArenaScreen(data.screen);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box>
      {floating ? (
        // Immersive game view (mobile): the full bar is hidden so the game
        // gets the whole viewport; a small floating button still opens the
        // navigation drawer so users can leave the page.
        //
        // It hides while that drawer is OPEN. Its z-index (1250) is above MUI's
        // drawer (1200), so it floated over the open menu and sat on top of the
        // INCRYPT logo in the drawer's own header. There is nothing for it to do
        // there either: the drawer has its own X, and tapping the backdrop
        // closes it.
        //
        // It also hides during a MATCH. There is nowhere at the top of a match
        // for it to go: measured at 375x812 the player's LP bar owns x 14..153,
        // the opponent's x 222..361, and the gap between them is 69px. Sitting
        // in that gap worked but left it hovering over live play, and top-left
        // — where it reads best — is exactly the player's own health bar.
        // On the menu the whole top-left corner is free, so that is where it
        // lives, and a match simply does not show it. Leaving mid-match is a
        // forfeit anyway: the game's own in-match MENU button is the way out,
        // and it returns here.
        <IconButton
          aria-label="Open navigation menu"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 8,
            left: 12,
            zIndex: 1250,
            display: mobileOpen || arenaScreen === 'game' ? 'none' : 'inline-flex',
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(4px)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.75)' }
          }}
        >
          <MenuIcon />
        </IconButton>
      ) : (
        <AppBar
          position="relative"
          elevation={0}
          sx={{
            /* Its own layer, above the page. The bar's backdrop-filter makes it
               a stacking context, and the route wrapper under it (.arena-fade)
               keeps its entrance transform, so the whole page was one layer
               painted AFTER the bar: the wallet menu, which hangs out of the
               bar, opened underneath the page — behind the collection's search
               field and "Show filters". A z-index on the menu cannot fix that;
               it only ranks the menu inside the bar. 1030, not MUI's appBar
               (1100): the wallet picker (.wallet-adapter-modal, 1040) must still
               cover the bar, as must the drawer (1200) and dialogs (1300).
               Nothing on a page goes above 50. */
            zIndex: 1030,
            background:
              'linear-gradient(180deg, rgba(16,26,39,0.92), rgba(7,11,18,0.96))',
            backdropFilter: 'blur(8px)',
            borderBottom: `1px solid ${PT.borderStrong}`,
            boxShadow: 'none',
            margin: 0,
            height: '64px',
            marginBottom: 0
          }}
        >
          {/* On a phone the bar has room for the menu button, the logo mark, the
              wallet button and the account button — not the INCRYPT wordmark as
              well. With it the row needed ~423px (~452px with a wallet
              connected), so on every phone the wallet button was squeezed until
              "Select Wallet" wrapped onto two lines inside its 40px box and
              vanished, and at 320px the account button was pushed off the screen.
              Below sm the wordmark is dropped (the drawer still shows the full
              logo) and the mark, the gaps and the wallet button's padding are a
              little smaller: ~287px, ~315px connected. The wallet button keeps
              its label on one line; scoped here because Header.css styles every
              wallet button in the app. From sm up nothing changes. */}
          <Toolbar
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              width: '100%',
              '& .wallet-adapter-button': {
                flexShrink: 0,
                whiteSpace: 'nowrap',
                px: { xs: 1.5, sm: '18px' }
              }
            }}
          >
            {isMobile ? (
              <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
            ) : null}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 1,
                flexShrink: 0
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Incrypt"
                sx={{
                  width: { xs: '44px', sm: '52px' },
                  height: { xs: '44px', sm: '52px' },
                  my: '6px'
                }}
              />
              <Typography
                component="span"
                sx={{
                  display: { xs: 'none', sm: 'inline' },
                  color: '#fff',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: 17, sm: 20 },
                  letterSpacing: '0.16em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                INCRYPT
              </Typography>
            </Box>

            {/* INC balance pill — desktop only; hidden on mobile to declutter
                the header (the balance is on the dashboard's My INC tile). */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: 0.7,
                  borderRadius: '999px',
                  border: `1px solid ${PT.border}`,
                  background: 'rgba(255,255,255,0.04)'
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    font: '700 10px Montserrat, sans-serif',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: PT.textMuted
                  }}
                >
                  INC Balance
                </Typography>
                <Typography
                  component="span"
                  noWrap
                  sx={{ fontWeight: 800, fontSize: 16, color: PT.tealText }}
                >
                  {formatTokenAmount(
                    (parseFloat(user.tokenAmount) + parseFloat(user.earnedTokens)).toString()
                  )}
                </Typography>
              </Box>
            )}

            <WalletMultiButton />

            <IconButton color="inherit" onClick={handleMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                alt={user.username ?? ''}
                src={profilePictureUrl}
                onError={() => setImageError(true)}
                sx={{
                  width: '2.75rem',
                  height: '2.75rem',
                  border: `2px solid ${PT.tealBorder}`,
                  boxShadow: '0 0 0 3px rgba(56,232,255,0.12)'
                }}
              >
                {user.username ? (
                  user.username.charAt(0).toUpperCase()
                ) : (
                  <AccountCircleIcon />
                )}
              </Avatar>
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.25,
              minWidth: 184,
              borderRadius: '14px',
              border: `1px solid ${PT.borderStrong}`,
              backgroundColor: PT.surface,
              backgroundImage:
                'linear-gradient(180deg, rgba(16,26,39,0.98), rgba(8,12,19,0.98))',
              boxShadow: '0 20px 46px -16px rgba(0,0,0,0.85)',
              color: PT.text,
              overflow: 'hidden',
              '& .MuiList-root': { py: 0.75 },
              '& .MuiMenuItem-root': {
                mx: 0.75,
                borderRadius: '10px',
                px: 1.5,
                py: 1.1,
                gap: 1.25,
                fontWeight: 700,
                fontSize: 14,
                color: PT.text,
                transition: 'background-color 0.14s ease',
                '&:hover': { backgroundColor: 'rgba(56,232,255,0.12)' }
              }
            }
          }
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleNavigation('settings');
          }}
        >
          <SettingsIcon sx={{ fontSize: 18, color: PT.teal }} />
          Settings
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={{ color: '#ff8a8a !important', '&:hover': { backgroundColor: 'rgba(255,93,93,0.12) !important' } }}
        >
          <LogoutIcon sx={{ fontSize: 18, color: '#ff8a8a' }} />
          Logout
        </MenuItem>
      </Menu>

      <Drawer
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true, disablePortal: true }}
        /* The paper used to take MUI's default light surface and centre its
           children VERTICALLY, which is why the last row (Marketplace) was cut
           off the bottom on a phone: a list longer than the screen centred
           itself and overflowed both ends. Dark surface, top-aligned, and the
           nav does its own scrolling. */
        sx={{
          '& .MuiDrawer-paper': {
            width: 'min(272px, 82vw)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            overflowX: 'hidden',
            backgroundColor: 'rgba(8, 14, 22, 0.97)',
            backgroundImage: 'none',
            borderRight: '1px solid rgba(56,232,255, 0.22)',
            color: '#fff'
          }
        }}
      >
        {/* Header row: the logo shares the top of the screen with the floating
            nav button that opened this, so it is left-aligned with its own
            close control on the right rather than centred underneath it. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            padding: '10px 12px 12px',
            paddingTop: 'calc(10px + env(safe-area-inset-top))',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            flexShrink: 0
          }}
        >
          <Box
            component="img"
            src="https://incrypt-dynamic-website-images.s3.ap-southeast-2.amazonaws.com/logo.png"
            alt="INCRYPT"
            sx={{ height: '30px', width: 'auto', display: 'block' }}
          />
          <IconButton
            onClick={handleDrawerToggle}
            aria-label="Close navigation menu"
            size="small"
            sx={{ color: 'rgba(255,255,255,0.65)' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ONE nav list, shared with the desktop rail.
            This used to be ~370 lines of hand-copied buttons, which is why the
            mobile menu silently lacked entries the sidebar had gained — Play
            (Dev) among them — and why tapping an item navigated but left the
            drawer sitting open over the page you had just asked for. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', paddingTop: '10px' }}>
          <Sidebar variant="drawer" onNavigate={handleDrawerToggle} />
        </Box>
      </Drawer>
    </Box>
  );
}

export default Header;
